import type {
  GpAssignmentMapPoint,
  SamplingPeriod,
} from '../../data-access/types/gp-sampling-assignment';

import type { AssignmentMapColorMode } from '../../lib/assignment-context';

export type NgpPeriodHighlight = 'now' | 'period' | 'neutral' | 'hidden';

/** Couleurs autorisées pour distinguer les préleveurs. Ordre écarté pour les ids consécutifs. */
const SAMPLER_PALETTE = [
  '#1565C0', // bleu
  '#FDD835', // jaune
  '#6D4C41', // marron
  '#00ACC1', // cyan
  '#E53935', // rouge
  '#8BC34A', // vert clair
  '#6A1B9A', // violet
  '#FB8C00', // orange
  '#212121', // noir
  '#EC407A', // rose
  '#1B5E20', // vert foncé
  '#C9A227', // doré
] as const;

export const SAMPLER_UNASSIGNED_COLOR = '#ced4da'; // gris, non renseigné

export const GP_PERIOD_FILL_COLORS: Record<SamplingPeriod, string> = {
  spring: '#51cf66',
  summer: '#fcc419',
  autumn: '#ff922b',
};

export const GP_PERIOD_UNKNOWN_COLOR = '#adb5bd';

export function samplerColorForId(samplerId: number): string {
  return SAMPLER_PALETTE[Math.abs(samplerId) % SAMPLER_PALETTE.length];
}

export function laboratoryFillColor(point: GpAssignmentMapPoint): string {
  if (point.laboratory) {
    return samplerColorForId(point.laboratory.id);
  }
  return SAMPLER_UNASSIGNED_COLOR;
}

export function samplerFillColor(
  point: GpAssignmentMapPoint,
  legendMode: 'organization' | 'user' = 'user',
): string {
  if (point.sampler) {
    if (point.sampler_organization?.color) {
      return point.sampler_organization.color;
    }
    if (legendMode === 'organization' && point.sampler_organization?.id != null) {
      return samplerColorForId(point.sampler_organization.id);
    }
    return samplerColorForId(point.sampler.id);
  }
  return SAMPLER_UNASSIGNED_COLOR;
}

export function periodFillColor(
  point: GpAssignmentMapPoint,
  selectedPeriod: SamplingPeriod,
  isNgp: boolean,
  highlight: NgpPeriodHighlight,
): string {
  if (isNgp) {
    if (highlight === 'hidden') {
      return GP_PERIOD_UNKNOWN_COLOR;
    }
    if (highlight === 'now') {
      return '#12b886';
    }
    if (highlight === 'period') {
      return GP_PERIOD_FILL_COLORS[selectedPeriod];
    }
    return GP_PERIOD_UNKNOWN_COLOR;
  }

  if (point.eligible_period === selectedPeriod) {
    return GP_PERIOD_FILL_COLORS[selectedPeriod];
  }
  return GP_PERIOD_UNKNOWN_COLOR;
}

export function pointFillColor(organicStatus: GpAssignmentMapPoint['organic_status']): string {
  switch (organicStatus) {
    case 'blocked':
    case 'deferred':
      return '#fd7e14';
    case 'expired':
      return '#e03131';
    case 'pending':
      return '#fab005';
    case 'ok':
      return '#40c057';
    default:
      return '#228be6';
  }
}

export function samplingFillColor(status: GpAssignmentMapPoint['status']): string {
  switch (status) {
    case 'FINISHED':
      return '#40c057';
    case 'IMPOSSIBLE':
      return '#e03131';
    case 'PENDING':
      return '#fab005';
    case 'INCOMPLETE':
    default:
      return '#adb5bd';
  }
}

export function pointIsAssigned(point: GpAssignmentMapPoint): boolean {
  return point.sampler != null;
}

export function ngpPeriodHighlight(
  point: GpAssignmentMapPoint,
  selectedPeriod: SamplingPeriod,
  mode: 'sms' | 'sampler',
): NgpPeriodHighlight {
  const periodList =
    mode === 'sms'
      ? point.assignable_periods
      : point.sampler_periods ?? point.samplable_periods;

  if (!periodList?.length && !point.samplable_periods?.length) {
    return 'neutral';
  }
  if (!periodList?.includes(selectedPeriod)) {
    return 'hidden';
  }
  if (point.samplable_now?.includes(selectedPeriod)) {
    return 'now';
  }
  return 'period';
}

export function pointFillOpacity(
  highlight: NgpPeriodHighlight,
  isNgp: boolean,
  visibleToSampler?: boolean | null,
): number {
  if (!isNgp) {
    return 1;
  }
  if (visibleToSampler === false) {
    return 0.35;
  }
  if (highlight === 'hidden') {
    return 0.12;
  }
  return 1;
}

export function pointStrokeColor(
  point: GpAssignmentMapPoint,
  highlight: NgpPeriodHighlight = 'neutral',
  isNgp = false,
): string {
  const assigned = pointIsAssigned(point);

  if (assigned && point.visible_to_sampler === false) {
    return '#868e96';
  }
  if (assigned) {
    return '#343a40';
  }
  if (!isNgp) {
    return '#ffffff';
  }
  if (highlight === 'now') {
    return '#12b886';
  }
  if (highlight === 'period') {
    return '#fab005';
  }
  return '#dee2e6';
}

export function pointStrokeWidth(
  point: GpAssignmentMapPoint,
  highlight: NgpPeriodHighlight = 'neutral',
  isNgp = false,
): number {
  if (pointIsAssigned(point)) {
    return 2.5;
  }
  if (isNgp && (highlight === 'now' || highlight === 'period')) {
    return 3;
  }
  return 1;
}

export const NGP_PERIOD_LEGEND_ITEMS = [
  { color: '#12b886', labelKey: 'legendPeriodNow' },
  { color: '#fab005', labelKey: 'legendPeriodWindow' },
] as const;
